import React from 'react';
import { Card, phone } from '@parsina/framework';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { genderKey } from '../../lib/patients.js';
import PatientAvatar from '../PatientAvatar.jsx';

// One register row as a mobile card.
//
// The desktop surface is the kit `Table` — four columns, a sticky header and a
// per-row name button, because a workstation reads a register by column. A
// phone cannot show four columns at 320px without either truncating every one
// of them or scrolling sideways, so the same four facts stack: identity on the
// first line, the two identifiers on the second.
//
// The WHOLE card is the tap target — the kit `Card`'s onClick form, a real
// <button> with `role="link"` because the tap navigates. The desktop's
// mouse-only `<tr onClick>` (the bug patientsRegister.test.jsx pins) cannot
// recur here: there is no row, only a button.

const MobilePatientCard = ({ patient, onOpen, testId }) => {
  const { t, formatDigits } = useTranslation();
  const gKey = genderKey(patient.gender);

  const sub = [
    gKey ? t(gKey) : patient.gender || '',
    patient.age != null ? t('common.units.years', { count: patient.age }) : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card
      onClick={() => onOpen?.(patient)}
      role="link"
      aria-label={t('today.row.openFor', { name: patient.name })}
      data-testid={testId}
      style={{ padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '0.875rem' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', minWidth: 0 }}>
        <PatientAvatar name={patient.name} src={patient.avatarUrl} size={36} bg={C.navy} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            dir="auto"
            style={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: C.textPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {patient.name}
          </div>
          {sub && (
            <div style={{ fontSize: '0.6875rem', color: C.textMuted }}>{sub}</div>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          marginTop: '0.5rem',
          fontSize: '0.6875rem',
          color: C.textMuted,
          flexWrap: 'wrap',
        }}
      >
        <span>
          {t('common.labels.phone')}{': '}
          {patient.phone ? (
            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{formatDigits(phone.forDisplay(patient.phone))}</span>
          ) : (
            t('common.labels.none')
          )}
        </span>
        <span style={{ marginInlineStart: 'auto' }}>
          {t('common.labels.fileNumber')}{`${t('common.punct.colon')} `}
          {patient.fileNumber ? formatDigits(patient.fileNumber) : t('common.labels.none')}
        </span>
      </div>
    </Card>
  );
};

export default MobilePatientCard;
